import React, { useState, PropsWithChildren, HTMLAttributes } from "react";
import { makeStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import { hexToColor4Array, color4ArrayToHex } from "../../utils/helpers";
import ToolsColorSwatch from "./ToolsColorSwatch";

import addColorSvg from '../../assets/icons/add-color.svg';

type ToolsColorSelectorProps = Omit<HTMLAttributes<HTMLElement>, 'color'> & PropsWithChildren<{
	onColorSelect: Function,
	color: any,
	transparent?: boolean,
	presetColors?: string[]
}>

export const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexDirection: "column",
		width: 208,
		alignItems: "center"
	},
	label: {
		fontWeight: "bold",
		textTransform: "uppercase",
		fontSize: 10,
		color: '#aaa'
	},
	spanGrid: {
		gridColumn: "1/-1"
	},
	swatchControl: {
		display: "grid",
		gridTemplateColumns: "repeat(7, 1fr)",
		gap: 10,
		width: "100%"
	},
	userSwatches: {
		minHeight: 54,
		backgroundColor: "#eeeeee",
		width: "calc(100% + 32px)",
		marginBottom: -16,
		marginTop: 16,
		borderTop: "1px solid #dddddd",
		padding: 16,
		boxSizing: "border-box",
		color: "#4a4a4a",
		borderBottomLeftRadius: 4,
		borderBottomRightRadius: 4
	},
	addUserColor: {
		width: 20,
		height: 20,
		cursor: "pointer",
		'& input': {
			opacity: 0,
			cursor: "pointer"
		}
	},
	addColorIcon: {
		width: 20,
		height: 20,
		cursor: "pointer",
		backgroundImage: `url(${addColorSvg})`,
		backgroundRepeat: "no-repeat",
		backgroundSize: "cover",
	}
}));

let userColors: string[] = JSON.parse(localStorage.getItem('userColors') || "[]");


// const colorOptions = [
// 	BrushColor.Red,
// 	BrushColor.Orange,
// 	BrushColor.Yellow,
// 	BrushColor.Green,
// 	BrushColor.Blue,
// 	BrushColor.Indigo,
// 	BrushColor.Violet,
// 	BrushColor.Black,
// 	BrushColor.Gray,
// 	BrushColor.White
// ];

const defaultColors = [
	"#0D62C5",
	"#00a7f4",
	"#eb1561",
	"#ffba5c",
	"#b8977e",
	"#fe8f52",
	"#76d353",

	"#f85f62",
	"#f67fb3",
	"#343f4b",
	"#5a6978",
	"#979faa",
	"#c6cbcf",
	"#ffffff"
];

const ToolsColorSelector = ({ presetColors = defaultColors, ...props }: ToolsColorSelectorProps) => {
	const classes = useStyles();
	const [newColor, setNewColor] = useState(undefined);
	const maxUserColors = 28;

	const updateColor = (c) => {
		const color = c === "transparent" ? [0, 0, 0, 0] : hexToColor4Array(c);
		if (props.onColorSelect) {
			props.onColorSelect(color);
		}
	}

	const addUserColor = (e) => {
		// Limit size of userColors array and prevent duplicate colors
		if (newColor) {
			if (!userColors.includes(newColor)) userColors = [newColor, ...userColors].slice(0, maxUserColors);
			localStorage.setItem('userColors', JSON.stringify(userColors));
		}
		setNewColor(undefined);
	}

	const updateNewColor = (c) => {
		// Clip number of user colors if at max to make room for newColor
		if (userColors.length >= maxUserColors) userColors = [c, ...userColors].slice(0, userColors.length - 1);
		setNewColor(c);
		updateColor(c);
	}

	if (props.transparent && presetColors[0] !== "transparent") {
		presetColors.unshift("transparent");
	}

	function isSelected(color) {
		return color === props.color;
		// switch(color) {
		// 	case "transparent": return props.color[3] === 0;
		// 	default: return color4ArrayToHex(props.color) === color;
		// }
	}

	return (
		<div className={`${classes.root}`}>
			<div className={classes.swatchControl}>
				<Typography className={`${classes.label} ${classes.spanGrid}`} variant="body2">Pick a swatch</Typography>
				{presetColors.map((c, i) =>
					<ToolsColorSwatch
						key={"color-" + i}
						color={c}
						isSelected={isSelected(c)}
						onClick={e => updateColor(c)}
					/>
				)}
			</div>
			<div className={`${classes.swatchControl} ${classes.userSwatches}`}>
				{newColor &&
					<ToolsColorSwatch
						color={newColor}
						isSelected={props.color === newColor}
					/>
				}
				{userColors.map((c, i) =>
					<ToolsColorSwatch
						key={"usercolor-" + i}
						color={c}
						isSelected={props.color === c}
						onClick={e => updateColor(c)}
					/>
				)}
				<label className={classes.addUserColor}>
					<div className={classes.addColorIcon} />
					<input
						type="color"
						value={props.color}
						onChange={e => updateNewColor(e.target.value)}
						onBlur={e => addUserColor(e)}
					/>
				</label>
			</div>
		</div>
	);
}

export default ToolsColorSelector;
